import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Objects the backend keeps an audit history for. The endpoint takes the domain
 * type name in the mandatory `objectType` parameter and answers 404 for
 * anything outside this set, so it is spelled out rather than left to the
 * caller — see `AuditContextEventModule.HANDLED_TYPES` in core.
 */
export const AUDIT_OBJECT_TYPES = {
  lineItem: 'LineItem',
  creative: 'Creative',
  order: 'Order',
  advertiser: 'Advertiser',
  politicalCampaign: 'PoliticalCampaign',
  politicalEntity: 'PoliticalEntity',
} as const

export type AuditObjectType = keyof typeof AUDIT_OBJECT_TYPES

/** The only audit classification the backend defines. */
export type AuditType = 'DIRECT_DELIVERY_IMPACT'

export interface AuditUserRef {
  id: string
  name: string
}

/**
 * One change described in the audit log. `text` is the English template the
 * backend ships with the `code`, with `{{placeholders}}` filled from
 * `parameters` — the same interpolation syntax i18next uses, so a UI can render
 * it as `t(code, { defaultValue: text, ...parameters })` and pick up a
 * translation when there is one.
 */
export interface AuditMessage {
  code: string
  text: string
  parameters: Record<string, string>
}

/** Everything one user changed on the object in a single save. */
export interface AuditEntry {
  timestamp: string
  objectId: string
  user: AuditUserRef | null
  /**
   * The operator behind the change when it was made while masquerading. Only
   * returned to a masquerading user, so it is absent for everyone else.
   */
  masqueraderUser: AuditUserRef | null
  messages: AuditMessage[]
}

export interface AuditHistoryPage {
  results: AuditEntry[]
  totalCount: number
  page: number
  pageSize: number
}

export interface AuditHistoryQuery {
  page?: number
  pageSize?: number
  /** Narrows to the changes of one classification. */
  auditType?: AuditType
  /** ISO instants, both inclusive of the range they bound. */
  startDate?: string
  endDate?: string
  /**
   * `SINCE_RUNNING_DATE` clamps the range to when the object started running.
   * Line items only — the endpoint answers 404 for any other type.
   */
  queryOption?: 'ALL' | 'SINCE_RUNNING_DATE'
}

function mapUserRef(v: unknown): AuditUserRef | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Record<string, unknown>
  return { id: (r.id as string) ?? '', name: (r.name as string) ?? '' }
}

function mapMessage(r: Record<string, unknown>): AuditMessage {
  return {
    code: (r.code as string) ?? '',
    text: (r.text as string) ?? '',
    parameters: (r.parameters as Record<string, string>) ?? {},
  }
}

function mapEntry(r: Record<string, unknown>): AuditEntry {
  return {
    timestamp: (r.timestamp as string) ?? '',
    objectId: ((r.object as Record<string, unknown>)?.id as string) ?? '',
    user: mapUserRef(r.user),
    masqueraderUser: mapUserRef(r.masqueraderUser),
    messages: ((r.messages as Record<string, unknown>[]) ?? []).map(mapMessage),
  }
}

export const AuditHistoryApi = {
  /**
   * The object's change history, newest first. Read-only: the endpoint is a GET
   * with no counterpart that writes.
   */
  getPage: async (
    objectType: AuditObjectType,
    objectId: string,
    { page = 1, pageSize = 25, auditType, startDate, endDate, queryOption }: AuditHistoryQuery = {}
  ): Promise<AuditHistoryPage> => {
    const params = new URLSearchParams({
      context: getContext(),
      objectType: AUDIT_OBJECT_TYPES[objectType],
      page: String(page),
      pageSize: String(pageSize),
      ...(auditType ? { auditType } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      ...(queryOption ? { queryOption } : {}),
    })
    const res = await apiFetch(`${API_BASE}/audithistory/${objectId}?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch audit history')
    const data = (await res.json()) as { results?: Record<string, unknown>[]; totalCount?: number }
    const results = (data.results ?? []).map(mapEntry)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },
}
